import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, lifecycle } from 'recompose';
import {
	Page,
	Section,
	Container,
	Group,
	Row,
	Column,
} from 'components/core/Page';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';

import { withRouteHandler, withErrorHandler } from 'decorators';
import { createBooking } from 'store/modules/bookings';
import unitSelector from 'store/selectors/unit';
import { change as changeFormData } from 'redux-form';

import { phonePropType } from 'config/propTypes';

import { H1, Text } from 'components/core/Typography';
import { Notification } from 'components/core/Notification';
import { OpenNow } from 'components/facility/OpenNow';
import { UnitBreadcrumbs } from 'components/unit/UnitBreadcrumbs';
import { UnitReservationForm } from 'components/unit/UnitReservationForm';
import { UnitSidebar } from 'components/unit/UnitSidebar';
import { ScarcityBanner } from 'components/search/ScarcityBanner';
import { ExpressBanner } from 'components/core/ExpressBanner';

import './UnitPage.scss';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

export const enhance = compose(
	connect(
		unitSelector,
		{ createBooking, changeFormData },
		mapDispatchToActions,
	),
	withErrorHandler,
	withRouteHandler((props) => !props.unit),
	lifecycle({
		componentDidMount() {
			this.props.actions.changeFormData(
				'unit-reservation',
				'moveInDate',
				null,
			);
		},
	}),
	withPageViewTracking(PAGE_TYPES.RESERVE),
);

export function UnitPage({
	actions,
	facility,
	outsideReservationWindow,
	phone,
	unit,
	trackingPixel,
	meta,
}) {
	const showOMI = facility.hasOnlineMoveins;
	let title = 'Complete Your Reservation';
	title = showOMI ? `${title} & Express Check-In` : title;

	return (
		<Page
			className="unit-page"
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.RESERVE}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
			noIndex
		>
			{outsideReservationWindow && (
				<Notification>
					Sorry, this facility only allows reservations for this unit
					for {unit.reservationWindow} days. Select another unit,
					facility, or an earlier move-in date.
				</Notification>
			)}
			<Group>
				<ScarcityBanner />
				{facility.isOpen && <OpenNow />}
			</Group>
			<Section>
				<Container>
					<UnitBreadcrumbs
						facility={facility}
						unit={unit}
					/>
					<H1
						className="reservation-header"
						size="mega"
					>
						{title}
					</H1>
					<Row reverse>
						<Column
							large={6}
							xlarge={4}
						>
							<UnitSidebar
								facility={facility}
								unit={unit}
								phone={phone}
							/>
						</Column>
						<Column
							large={6}
							xlarge={8}
						>
							<div className="card">
								<div className="card-header">
									<Text size="small">
										Lock in this rate now. No credit card,
										no obligation.
									</Text>
								</div>
								<div className="card-block">
									{showOMI && (
										<div className="express-banner">
											<ExpressBanner />
										</div>
									)}
									<UnitReservationForm
										onSubmit={actions.createBooking}
										unit={unit}
										facility={facility}
									/>
								</div>
							</div>
						</Column>
					</Row>
				</Container>
			</Section>
		</Page>
	);
}

UnitPage.propTypes = {
	actions: PropTypes.shape({
		createBooking: PropTypes.func.isRequired,
	}).isRequired,
	facility: PropTypes.object,
	unit: PropTypes.object,
	outsideReservationWindow: PropTypes.bool,
	phone: phonePropType,
	trackingPixel: PropTypes.string.isRequired,
	meta: PropTypes.object,
};

export default enhance(UnitPage);
